"use client";

import Link from "next/link";
import { compressDriverDocument } from "@/lib/compressDriverDocument";
import { useEffect, useRef, useState } from "react";

type Point = { latitude: number; longitude: number; label: string };
type Ride = { id: number; name: string; from: string; to: string };
type Details = {
  id: number; availableSeats: number; contribution: number | null;
  status: string; rideDate: string; isOwnRide: boolean;
  calculatedFare: {
    farePerSeat: number;
    totalFare: number;
    passengerDistanceKm: number;
  } | null;
};
type Props = {
  ride: Ride; pickup: Point; destination: Point;
  onClose: () => void; onBooked: () => void;
};

export default function RideBookingConfirmation({ ride, pickup, destination, onClose, onBooked }: Props) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const pendingRef = useRef(false);
  const [details, setDetails] = useState<Details | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [loadedAt, setLoadedAt] = useState(0);
  const [seats, setSeats] = useState(1);
  const [bookingType, setBookingType] = useState<"Seat" | "Parcel">("Seat");
  const [parcelDescription, setParcelDescription] = useState("");
  const [parcelWeightKg, setParcelWeightKg] = useState("");
  const [receiverPhone, setReceiverPhone] = useState("");
  const [parcelPhoto, setParcelPhoto] = useState<File | null>(null);
  const [compressingPhoto, setCompressingPhoto] = useState(false);
  const parcelCompressionId = useRef(0);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");
  const [needsLogin, setNeedsLogin] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [booked, setBooked] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (dialog && !dialog.open) dialog.showModal();
    return () => dialog?.close();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    async function load() {
      setLoading(true);
      setError("");
      setNeedsLogin(false);
      setDetails(null);
      try {
        const response = await fetch(`/api/rides/${ride.id}/booking-details?${new URLSearchParams({
          pickupLat: String(pickup.latitude),
          pickupLng: String(pickup.longitude),
          dropLat: String(destination.latitude),
          dropLng: String(destination.longitude),
        })}`, {
          cache: "no-store", signal: controller.signal,
        });
        const data = await response.json();
        if (controller.signal.aborted) return;
        if (response.status === 401) setNeedsLogin(true);
        if (!response.ok) throw new Error(data.message || "Unable to load booking details.");
        if (data.id !== ride.id || !Number.isInteger(data.availableSeats) ||
            !Number.isFinite(Date.parse(data.rideDate)) ||
            (data.contribution !== null && (typeof data.contribution !== "number" || !Number.isFinite(data.contribution)))) {
          throw new Error("Unable to load booking details.");
        }
        setLoadedAt(Date.now());
        setDetails(data);
      } catch (failure) {
        if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : "Unable to load booking details.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    void load();
    return () => controller.abort();
  }, [ride.id, pickup.latitude, pickup.longitude, destination.latitude, destination.longitude, loadAttempt]);

  const amount = details?.calculatedFare?.farePerSeat ?? NaN;
  const validOffer = Number.isFinite(amount) && amount >= 0;
  const available = !!details && !details.isOwnRide && details.status === "Open" &&
    details.availableSeats >= seats && Number.isInteger(seats) && seats >= 1 &&
    Date.parse(details.rideDate) > loadedAt;
  const validPoint = (point: Point) => Number.isFinite(point.latitude) && Math.abs(point.latitude) <= 90 &&
    Number.isFinite(point.longitude) && Math.abs(point.longitude) <= 180;
  const parcelWeight = Number(parcelWeightKg);

  const validParcel =
    parcelDescription.trim().length > 0 &&
    parcelDescription.trim().length <= 500 &&
    parcelWeightKg.trim() !== "" &&
    Number.isFinite(parcelWeight) &&
    parcelWeight >= 0.1 &&
    parcelWeight <= 100 &&
    /^[6-9][0-9]{9}$/.test(receiverPhone) &&
    !compressingPhoto &&
    parcelPhoto !== null &&
    parcelPhoto.size > 0 &&
    parcelPhoto.size <= 100 * 1024 &&
    ["image/jpeg", "image/png", "image/webp"].includes(parcelPhoto.type);

  const canConfirm =
    available &&
    (bookingType === "Parcel" ? validParcel : validOffer) &&
    validPoint(pickup) &&
    validPoint(destination) &&
    !loading && !pending && !booked && !uncertain && !needsLogin;

  function close() {
    if (pendingRef.current) return;
    if (booked || uncertain) onBooked();
    else onClose();
  }

  async function confirm() {
    // Synchronous lock also prevents two clicks before React renders pending state.
    if (!canConfirm || pendingRef.current) return;
    if (!details || Date.parse(details.rideDate) <= Date.now()) {
      setError("This departure time has passed. Close and search again.");
      return;
    }
    pendingRef.current = true;
    setPending(true);
    setError("");
    try {
      let uploadedParcelImage: string | null = null;

      if (bookingType === "Parcel") {
        if (!validParcel || !parcelPhoto) {
          setError("Please complete all parcel details and select a valid photo.");
          return;
        }

        const formData = new FormData();
        formData.append("file", parcelPhoto);

        const uploadResponse = await fetch("/api/parcel-upload", {
          method: "POST",
          body: formData,
        });

        const uploadData = await uploadResponse.json();

        if (!uploadResponse.ok || typeof uploadData.image !== "string") {
          setError(uploadData.message || "Parcel photo upload failed.");
          return;
        }

        uploadedParcelImage = uploadData.image;
      }

      const response = await fetch(`/api/rides/${ride.id}/booking`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          seats: bookingType === "Parcel" ? 1 : seats,
          bookingType,
          ...(bookingType === "Parcel"
            ? {
                parcelDescription: parcelDescription.trim(),
                parcelWeightKg: Number(parcelWeightKg),
                receiverPhone,
                parcelImage: uploadedParcelImage,
              }
            : { passengerOfferPrice: amount }),
          pickupLocation: pickup.label, pickupLatitude: pickup.latitude, pickupLongitude: pickup.longitude,
          destinationLocation: destination.label, destinationLatitude: destination.latitude, destinationLongitude: destination.longitude,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 401) setNeedsLogin(true);
        // Server/network failures may happen after a commit. Never automatically resubmit.
        if (response.status >= 500) setUncertain(true);
        setError(data.message || "Unable to confirm booking.");
        return;
      }
      if (data.success !== true || !Number.isSafeInteger(data.bookingId)) {
        throw new Error("Booking response could not be verified.");
      }
      setBooked(true);
    } catch {
      setUncertain(true);
      setError("Booking result could not be confirmed. Check My Rides before trying again.");
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  }

  return (
    <dialog ref={dialogRef} aria-labelledby="ride-booking-title"
      onCancel={(event) => { event.preventDefault(); close(); }}
      className="fixed inset-0 m-auto max-h-[75dvh] w-[calc(100%-1.5rem)] max-w-md overflow-y-auto rounded-2xl border border-indigo-100 bg-white p-4 text-slate-900 shadow-2xl backdrop:bg-slate-900/40">
      <div className="flex items-center justify-between gap-3">
        <h2 id="ride-booking-title" className="text-sm font-bold">{booked ? "Request sent" : "Request a seat"}</h2>
        <button type="button" onClick={close} disabled={pending} className="rounded px-2 py-1 text-xs font-semibold text-indigo-700 disabled:opacity-50">Close</button>
      </div>
      <p className="mt-2 break-words text-xs font-semibold">{ride.name}: {ride.from} → {ride.to}</p>
      <dl className="mt-3 space-y-2 text-xs">
        <div><dt className="font-semibold">Your pickup</dt><dd className="break-words">{pickup.label}</dd></div>
        <div><dt className="font-semibold">Your destination</dt><dd className="break-words">{destination.label}</dd></div>
      </dl>
      {loading && <p role="status" className="mt-3 text-xs">Loading booking details...</p>}
      {details && !booked && <div className="mt-3 space-y-3">
        <p className="text-xs">Departure: {new Intl.DateTimeFormat("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }).format(new Date(details.rideDate))}</p>
        {details.isOwnRide && <p className="text-xs text-red-700">You cannot book your own ride.</p>}
        {!available && !details.isOwnRide && <p className="text-xs text-red-700">This ride or selected seats are no longer available. Close and search again.</p>}
        <div className="space-y-2">
          <p className="text-xs font-bold">Booking Type</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              disabled={pending || uncertain}
              onClick={() => setBookingType("Seat")}
              className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                bookingType === "Seat"
                  ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 bg-white text-slate-700"
              }`}
            >
              🪑 Seat
            </button>
            <button
              type="button"
              disabled={pending || uncertain}
              onClick={() => setBookingType("Parcel")}
              className={`rounded-lg border px-3 py-2 text-sm font-semibold ${
                bookingType === "Parcel"
                  ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                  : "border-slate-200 bg-white text-slate-700"
              }`}
            >
              📦 Parcel
            </button>
          </div>
        </div>

        {bookingType === "Seat" ? (
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="homepage-booking-seats" className="text-xs font-semibold">Seats</label>
          <select id="homepage-booking-seats" value={seats} disabled={pending || uncertain}
            onChange={(event) => setSeats(Number(event.target.value))} className="rounded-md border border-slate-200 px-2 py-1 text-xs">
            {Array.from({ length: Math.max(0, Math.min(20, details.availableSeats)) }, (_, index) => index + 1).map((count) => <option key={count} value={count}>{count}</option>)}
          </select>
          <span className="text-xs text-slate-500">{details.availableSeats} available</span>
        </div>
        ) : (
          <div className="space-y-3 rounded-lg border border-indigo-100 bg-white p-3">
            <label className="block text-xs font-semibold">
              Parcel Details
              <textarea
                value={parcelDescription}
                onChange={(event) => setParcelDescription(event.target.value)}
                maxLength={500}
                placeholder="What is inside the parcel?"
                className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm"
              />
            </label>

            <label className="block text-xs font-semibold">
              Approximate Weight (kg)
              <input
                type="number"
                min="0.1"
                max="100"
                step="0.1"
                value={parcelWeightKg}
                onChange={(event) => setParcelWeightKg(event.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm"
              />
            </label>

            <label className="block text-xs font-semibold">
              📞 Receiver Mobile Number
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                value={receiverPhone}
                onChange={(event) =>
                  setReceiverPhone(event.target.value.replace(/\D/g, "").slice(0, 10))
                }
                placeholder="10-digit receiver mobile number"
                className="mt-1 w-full rounded-lg border border-slate-200 p-2 text-sm"
              />
            </label>

            <label className="block text-xs font-semibold">
              📸 Parcel Photo
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => {
                  const file = event.target.files?.[0] ?? null;
                  const compressionId = ++parcelCompressionId.current;

                  setParcelPhoto(null);
                  setError("");

                  if (!file) {
                    setCompressingPhoto(false);
                    return;
                  }

                  setCompressingPhoto(true);

                  void compressDriverDocument(file)
                    .then((compressed) => {
                      if (compressionId !== parcelCompressionId.current) return;
                      setParcelPhoto(compressed);
                    })
                    .catch((cause: unknown) => {
                      if (compressionId !== parcelCompressionId.current) return;
                      setError(
                        cause instanceof Error
                          ? cause.message
                          : "Parcel photo compression failed."
                      );
                    })
                    .finally(() => {
                      if (compressionId === parcelCompressionId.current) {
                        setCompressingPhoto(false);
                      }
                    });
                }}
                className="mt-1 block w-full text-xs"
              />
              {compressingPhoto && (
                <span className="mt-1 block text-xs text-indigo-700">
                  Compressing parcel photo...
                </span>
              )}
              {parcelPhoto && (
                <span className="mt-1 block text-xs text-emerald-700">
                  Selected: {parcelPhoto.name}
                </span>
              )}
            </label>

            <p className="text-xs text-amber-700">
              Complete all parcel details and select a JPG, PNG or WebP photo under 100 KB to send your request.
            </p>
          </div>
        )}
        {bookingType === "Seat" ? (
          <>
        <div className="rounded-lg bg-indigo-50 p-3 text-xs">
          <p className="font-semibold">Automatic route-distance fare</p>
          {details.calculatedFare ? (
            <>
              <p className="mt-1 font-bold">₹{amount.toFixed(2)} per seat</p>
              <p className="mt-1 text-slate-600">
                Your route distance: {details.calculatedFare.passengerDistanceKm.toFixed(2)} km
              </p>
              <p className="mt-1 text-slate-600">Driver full-route fare: ₹{details.contribution ?? 0}</p>
            </>
          ) : (
            <p className="mt-1 text-red-700">Fare unavailable for these locations. Booking request cannot be sent.</p>
          )}
        </div>
        {validOffer && <p className="text-xs font-semibold">Total contribution: ₹{(Math.round(amount * seats * 100) / 100).toFixed(2)}</p>}
        {!validOffer && <p className="text-xs text-red-700">Automatic fare could not be calculated.</p>}
          </>
        ) : (
          <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs">
            <p className="font-bold text-indigo-900">📦 Parcel Fare</p>
            <p className="mt-1 text-indigo-700">
              Driver will offer a price after reviewing parcel details.
            </p>
            <p className="mt-1 text-slate-600">
              You can accept or reject the driver's price before booking proceeds.
            </p>
          </div>
        )}
        <p className="rounded-lg bg-emerald-50 p-2 text-xs text-emerald-800">No advance payment required. Pay the driver directly at your destination.</p>
      </div>}
      {bookingType === "Parcel" && !canConfirm && !booked && !pending && !uncertain && (
        <p className="mt-2 text-xs text-amber-700">
          {compressingPhoto
            ? "Please wait while the parcel photo is compressed."
            : !parcelDescription.trim()
            ? "Enter parcel description."
            : parcelDescription.trim().length > 500
            ? "Parcel description must be 500 characters or less."
            : !parcelWeightKg.trim() || !Number.isFinite(parcelWeight) || parcelWeight < 0.1 || parcelWeight > 100
            ? "Enter parcel weight between 0.1 and 100 kg."
            : !/^[6-9][0-9]{9}$/.test(receiverPhone)
            ? "Enter a valid 10-digit receiver mobile number."
            : !parcelPhoto
            ? "Select a parcel photo."
            : parcelPhoto.size > 100 * 1024
            ? "Photo is too large. Select an image under 100 KB."
            : !["image/jpeg", "image/png", "image/webp"].includes(parcelPhoto.type)
            ? "Use a JPG, PNG or WebP photo."
            : !available
            ? "This ride is not available for booking. Check its date and available seats."
            : loading
            ? "Checking ride availability..."
            : needsLogin
            ? "Log in to send a parcel request."
            : "Please check pickup and destination locations."}
        </p>
      )}
      {error && <p role="alert" className="mt-3 text-xs text-red-700">{error}</p>}
      {!loading && !details && !needsLogin && <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)} className="mt-2 text-xs font-semibold text-indigo-700">Retry loading details</button>}
      {needsLogin && <Link href="/login" className="mt-3 block text-xs font-semibold text-indigo-700">Log in to book a ride</Link>}
      {booked && <p role="status" className="mt-3 text-xs text-emerald-800">Booking request sent. Waiting for DRIVER approval. Phone numbers will be available after acceptance.</p>}
      {(booked || uncertain) ? <Link href="/my-rides" className="mt-3 block rounded-lg bg-indigo-600 p-2 text-center text-sm font-semibold text-white">View My Rides</Link> :
        <button type="button" onClick={() => void confirm()} disabled={!canConfirm} className="mt-3 w-full rounded-lg bg-indigo-600 p-2 text-sm font-semibold text-white disabled:opacity-50">{pending ? (bookingType === "Parcel" ? "Sending parcel request..." : "Confirming booking...") : (bookingType === "Parcel" ? "Send Parcel Request" : "Confirm Booking")}</button>}
    </dialog>
  );
}
